import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import api from "../lib/api";

const SID = "lola-" + Math.random().toString(36).slice(2);

export default function LolaWidget({ mode }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const isList = mode === "list";
  const title = isList ? "LOLA · LEVERAGE STRATEGIST" : "LOLA · LIFESTYLE CONCIERGE";
  const intro = isList
    ? "I read your network for the leverage inside it. Ask me who you should know next — and why."
    : "I curate for the life you're living now. Tell me what your life needs next.";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const m = input.trim(); setInput("");
    setMsgs((p) => [...p, { role: "user", text: m }]); setBusy(true);
    try {
      const { data } = await api.post("/lola/chat", { mode, message: m, session_id: SID });
      setMsgs((p) => [...p, { role: "lola", text: data.reply }]);
    } catch { setMsgs((p) => [...p, { role: "lola", text: "I'm here — try me again in a moment." }]); }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="lola-toggle" onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-ink text-goldbright border-2 border-goldbright ring-1 ring-goldbright/30 shadow-2xl hover:bg-goldbright hover:text-ink transition-colors">
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] flex flex-col bg-obsidian border border-gold/40 text-ivory">
            <div className="px-5 py-4 border-b border-gold/30">
              <p className="eyebrow !text-[0.6rem]">{title}</p>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-4 space-y-4">
              <p className="font-headline italic text-lg text-ivory/80 leading-snug">{intro}</p>
              {msgs.map((m, i) => (
                <div key={i} data-testid={`lola-msg-${m.role}`} className={m.role === "user" ? "text-right" : ""}>
                  <span className={`inline-block max-w-[85%] px-4 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-gold/15 text-ivory" : "bg-white/5 text-ivory/90 border-l border-gold/50"}`}>{m.text}</span>
                </div>
              ))}
              {busy && <p className="eyebrow !text-[0.55rem] animate-pulse">Lola is thinking…</p>}
              <div ref={endRef} />
            </div>
            <div className="p-4 border-t border-gold/30 flex gap-2">
              <input data-testid="lola-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Lola…"
                className="flex-1 bg-transparent border border-gold/30 px-3 py-2 text-sm outline-none focus:border-gold" />
              <button data-testid="lola-send" onClick={send} className="px-3 border border-gold text-gold hover:bg-gold hover:text-ink transition-colors"><Send size={16} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
